import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getProducts } from '../services/ProductService';
import { getCategories } from '../services/CategoryService';
import { getFlavors } from '../services/FlavorService';
import { getTable } from '../services/TableService';
import { api } from '../utils/api';
import { formatCurrency } from '../utils/format-currency';
import { 
  ShoppingBag, 
  ChevronLeft, 
  Plus, 
  Minus, 
  Trash2, 
  CheckCircle2,
  UtensilsCrossed
} from 'lucide-react';
import { toast } from 'react-toastify';
import FlavorSelectorModal from '../components/Flavors/FlavorSelectorModal';
import type { Product } from '../types/@types/product';

const PublicMenu = () => {
  const { tableId } = useParams<{ tableId: string }>();
  const [cart, setCart] = useState<any[]>([]);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [flavorContext, setFlavorContext] = useState<{ product: Product } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);

  const { data: table } = useQuery({
    queryKey: ['publicTable', tableId],
    queryFn: () => getTable(tableId!),
    enabled: !!tableId,
  });

  const { data: products, isLoading: loadingProducts } = useQuery({
    queryKey: ['publicProducts'],
    queryFn: getProducts,
  });

  const { data: categories } = useQuery({
    queryKey: ['publicCategories'],
    queryFn: getCategories,
  });

  // We don't strictly need useQuery for flavors here as FlavorSelectorModal might fetch them or use them from context, 
  // but let's keep it if we want to show anything related.
  useQuery({
      queryKey: ['publicFlavors'],
      queryFn: getFlavors
  });

  const filteredProducts = useMemo(() => {
    if (!products) return [];
    if (!activeCategory) return products;
    return products.filter(p => 
      typeof p.category === 'string' ? p.category === activeCategory : p.category?.id === activeCategory
    );
  }, [products, activeCategory]);

  const addToCart = (product: Product, selectedFlavors?: any[]) => {
    const cartKey = `${product.id}-${(selectedFlavors || []).map(f => `${f.id}:${f.qty}`).join(',')}`;
    
    setCart(prev => {
      const existing = prev.find(item => item.cartKey === cartKey);
      if (existing) {
        return prev.map(item => 
          item.cartKey === cartKey ? { ...item, qty: item.qty + 1 } : item
        );
      }
      return [...prev, { 
        cartKey, 
        product, 
        qty: 1, 
        flavors: selectedFlavors || [],
        price: product.price + (selectedFlavors || []).reduce((acc, f) => acc + (f.price_delta || 0) * f.qty, 0)
      }];
    });
    
    toast.success(`${product.name} agregado`);
  };

  const handleProductClick = (product: Product) => {
    const hasFlavors = (product.maxFlavors && product.maxFlavors > 0) || (product.flavors?.enabled);
    if (hasFlavors) {
      setFlavorContext({ product });
    } else {
      addToCart(product);
    }
  };

  const removeFromCart = (cartKey: string) => {
    setCart(prev => prev.filter(item => item.cartKey !== cartKey));
  };

  const updateQty = (cartKey: string, delta: number) => {
    setCart(prev => prev.map(item => {
      if (item.cartKey === cartKey) {
        const newQty = Math.max(1, item.qty + delta);
        return { ...item, qty: newQty };
      }
      return item;
    }));
  };

  const cartTotal = cart.reduce((acc, item) => acc + (item.price * item.qty), 0);

  const submitOrder = async () => {
    if (cart.length === 0) return;
    setIsSubmitting(true);
    try {
      const payload = {
        tableId,
        items: cart.map(item => ({
          product: item.product.id,
          quantity: item.qty,
          flavors: item.flavors.map((f: any) => ({
            flavorId: f.id,
            qty: f.qty
          }))
        }))
      };

      await api.post('/orders/self-order', payload);
      setOrderSuccess(true);
      setCart([]);
      setIsCartOpen(false);
      toast.success("¡Pedido enviado correctamente!");
    } catch (error) {
      console.error(error);
      toast.error("Error al enviar el pedido. Intenta de nuevo.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (orderSuccess) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#0d1221] p-6 text-center text-white">
        <div className="mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-500 animate-bounce">
          <CheckCircle2 size={64} />
        </div>
        <h1 className="mb-2 text-3xl font-bold bg-gradient-to-r from-white to-slate-400 bg-clip-text text-transparent">¡Pedido Recibido!</h1>
        <p className="mb-8 text-slate-400">Estamos preparando tus delicias. <br/> El personal se acercará pronto.</p>
        <button 
          onClick={() => setOrderSuccess(false)}
          className="rounded-full bg-indigo-600 px-8 py-3 font-semibold text-white shadow-xl shadow-indigo-600/40 transition-all hover:bg-indigo-50 hover:text-indigo-600 active:scale-95"
        >
          Hacer otro pedido
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d1221] text-slate-200">
      {/* Header Premium */}
      <header className="sticky top-0 z-40 bg-[#0d1221]/80 backdrop-blur-xl border-b border-white/5 px-6 py-5">
        <div className="flex items-center justify-between max-w-2xl mx-auto">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-600 shadow-lg shadow-indigo-900/40 text-white">
              <UtensilsCrossed size={24} />
            </div>
            <div>
              <h1 className="text-xl font-black text-white tracking-tight">DufiWaiter</h1>
              <div className="flex items-center gap-2">
                <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></span>
                <p className="text-xs text-indigo-400 font-bold uppercase tracking-widest">Mesa {table?.number || tableId}</p>
              </div>
            </div>
          </div>
          <button 
            onClick={() => setIsCartOpen(true)}
            className="group relative flex h-12 w-12 items-center justify-center rounded-full bg-white/5 text-slate-200 border border-white/10 transition-all hover:bg-white/10 active:scale-90"
          >
            <ShoppingBag size={22} />
            {cart.length > 0 && (
              <span className="absolute -top-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-indigo-600 text-[11px] font-black text-white border-2 border-[#0d1221]">
                {cart.reduce((acc, i) => acc + i.qty, 0)}
              </span>
            )}
          </button>
        </div>
      </header>

      {/* Categories Bar */}
      <div className="sticky top-[89px] z-30 flex gap-3 overflow-x-auto bg-[#0d1221]/80 backdrop-blur-xl px-4 py-4 no-scrollbar border-b border-white/5">
        <button
          onClick={() => setActiveCategory(null)}
          className={`flex-shrink-0 whitespace-nowrap rounded-2xl px-6 py-2.5 text-sm font-bold transition-all ${
            activeCategory === null 
              ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-900/30' 
              : 'bg-white/5 text-slate-400 hover:bg-white/10'
          }`}
        >
          Todo el Menú
        </button>
        {categories?.map((cat: any) => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            className={`flex-shrink-0 whitespace-nowrap rounded-2xl px-6 py-2.5 text-sm font-bold transition-all ${
              activeCategory === cat.id 
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-900/30' 
                : 'bg-white/5 text-slate-400 hover:bg-white/10'
            }`}
          >
            {cat.name}
          </button>
        ))}
      </div>

      {/* Main Content */}
      <main className="px-6 py-8 pb-32 max-w-2xl mx-auto">
        <div className="mb-8">
          <h2 className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-1">Especialidades</h2>
          <p className="text-3xl font-black text-white">Nuestra Selección</p>
        </div>

        {loadingProducts ? (
           <div className="flex h-64 flex-col items-center justify-center gap-6">
              <div className="relative">
                <div className="h-16 w-16 rounded-full border-4 border-indigo-600/20"></div>
                <div className="absolute top-0 h-16 w-16 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent"></div>
              </div>
              <p className="text-slate-500 font-bold animate-pulse uppercase tracking-wider text-xs">Cocinando el menú...</p>
           </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {filteredProducts.map((product) => (
              <div 
                key={product.id}
                className="group relative overflow-hidden flex flex-col gap-4 rounded-3xl bg-white/5 p-5 border border-white/10 transition-all hover:bg-white/10 hover:border-indigo-500/50 hover:translate-y-[-2px] active:scale-[0.98]"
                onClick={() => handleProductClick(product)}
              >
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                       <span className="px-2 py-0.5 rounded-md bg-indigo-600/20 text-[10px] text-indigo-400 font-black uppercase tracking-tighter">Destacado</span>
                    </div>
                    <h3 className="font-bold text-white text-xl group-hover:text-indigo-400 transition-colors tracking-tight">{product.name}</h3>
                    <p className="mt-2 text-sm text-slate-400 leading-relaxed line-clamp-2">{product.description || "Inspirado en los mejores sabores tradicionales con un toque moderno."}</p>
                    <div className="mt-4 flex items-center justify-between">
                       <p className="text-2xl font-black text-white tracking-tighter">{formatCurrency(product.price)}</p>
                       <div className="h-10 w-10 flex items-center justify-center rounded-2xl bg-indigo-600 text-white shadow-lg shadow-indigo-900/40 group-hover:scale-110 transition-transform">
                          <Plus size={20} />
                       </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Glassmorphism Cart Sheet */}
      {isCartOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-[#0d1221]/90 backdrop-blur-md transition-all animate-in fade-in duration-300">
          <div 
            className="w-full max-w-lg rounded-t-[40px] bg-[#1a2236] p-8 pb-10 shadow-[0_-20px_40px_rgba(0,0,0,0.5)] border-t border-white/10 animate-in slide-in-from-bottom duration-500"
          >
            <div className="mx-auto mb-6 h-1.5 w-16 rounded-full bg-slate-700/50"></div>
            
            <div className="mb-8 flex items-center justify-between">
              <div>
                <h2 className="text-3xl font-black text-white tracking-tight">Tu Pedido</h2>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-widest mt-1">Listo para enviar</p>
              </div>
              <button 
                onClick={() => setIsCartOpen(false)}
                className="flex h-12 w-12 items-center justify-center rounded-full bg-white/5 text-slate-400 hover:text-white border border-white/10"
              >
                <ChevronLeft className="rotate-[-90deg]" size={28} />
              </button>
            </div>

            <div className="max-h-[45dvh] overflow-y-auto pr-2 no-scrollbar mb-8">
              {cart.length === 0 ? (
                <div className="py-20 text-center">
                  <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-3xl bg-white/5 text-slate-700 border border-white/5">
                    <ShoppingBag size={40} />
                  </div>
                  <p className="text-slate-500 font-bold uppercase tracking-wider">Nada en el carrito</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {cart.map((item) => (
                    <div key={item.cartKey} className="flex gap-5 rounded-3xl bg-white/5 p-4 border border-white/5">
                      <div className="flex-1">
                        <h4 className="font-bold text-white text-lg tracking-tight">{item.product.name}</h4>
                        {item.flavors.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                             {item.flavors.map((f: any) => (
                               <span key={f.id} className="px-2 py-0.5 rounded-lg bg-indigo-600/10 text-[10px] text-indigo-400 font-bold border border-indigo-500/20">
                                 {f.name} x{f.qty}
                               </span>
                             ))}
                          </div>
                        )}
                        <p className="mt-3 text-sm font-black text-indigo-400">{formatCurrency(item.price * item.qty)}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <button 
                          onClick={() => updateQty(item.cartKey, -1)}
                          className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/5 text-white hover:bg-rose-500/20 hover:text-rose-400 transition-all border border-white/5"
                        >
                          {item.qty === 1 ? <Trash2 size={18} /> : <Minus size={18} />}
                        </button>
                        <span className="w-6 text-center text-lg font-black text-white">{item.qty}</span>
                        <button 
                          onClick={() => updateQty(item.cartKey, 1)}
                          className="flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-600 text-white shadow-lg shadow-indigo-900/40 border border-indigo-500/50"
                        >
                          <Plus size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-6">
              <div className="flex items-center justify-between text-white">
                <span className="text-slate-400 font-bold uppercase tracking-widest text-xs">Total del Pedido</span>
                <span className="text-3xl font-black tracking-tighter">{formatCurrency(cartTotal)}</span>
              </div>
              <button
                disabled={cart.length === 0 || isSubmitting}
                onClick={submitOrder}
                className="w-full relative overflow-hidden group rounded-3xl bg-indigo-600 py-5 font-black text-white shadow-2xl shadow-indigo-900/60 transition-all hover:bg-indigo-500 disabled:opacity-50 disabled:grayscale active:scale-[0.98]"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center gap-3">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                    <span className="uppercase tracking-widest text-sm">Procesando...</span>
                  </div>
                ) : (
                  <span className="uppercase tracking-[0.2em] text-sm font-black">Confirmar y Ordenar</span>
                )}
                <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Action Bar */}
      {cart.length > 0 && !isCartOpen && (
        <div className="fixed bottom-10 left-1/2 z-40 -translate-x-1/2 w-[90%] max-w-md animate-in slide-in-from-bottom duration-700">
          <button 
            onClick={() => setIsCartOpen(true)}
            className="flex w-full items-center justify-between rounded-[28px] bg-indigo-600 p-2 pl-6 text-white shadow-[0_20px_50px_rgba(79,70,229,0.5)] border border-indigo-500/50 transition-all active:scale-95"
          >
            <div className="flex items-center gap-4">
               <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/60 text-left">Carrito</p>
                  <p className="font-black text-lg tracking-tight">{cart.reduce((acc, i) => acc + i.qty, 0)} Items</p>
               </div>
            </div>
            <div className="flex items-center gap-4 bg-white/10 h-16 rounded-[22px] px-6 border border-white/10">
               <p className="text-2xl font-black tracking-tighter">{formatCurrency(cartTotal)}</p>
               <ChevronLeft className="rotate-180" size={24} />
            </div>
          </button>
        </div>
      )}

      {/* Modal Integration */}
      {flavorContext && (
        <FlavorSelectorModal
          open={!!flavorContext}
          onClose={() => setFlavorContext(null)}
          product={flavorContext.product}
          onConfirm={(payload) => {
            addToCart(flavorContext.product, payload.flavors);
            setFlavorContext(null);
          }}
        />
      )}
    </div>
  );
};

export default PublicMenu;
